import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, MessageCircle, Phone, User, Package, Hash, X } from 'lucide-react';
import ENV from '../config/env';

// ContactForm component - Displays modal for WhatsApp quote requests
const ContactForm = ({ isOpen, onClose, initialProduct = '' }) => {
    // Form state management
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        message: '',
        product: initialProduct,
        units: '1'
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Update product when initialProduct prop changes
    useEffect(() => {
        if (initialProduct) {
            setFormData(prev => ({ ...prev, product: initialProduct }));
        }
    }, [initialProduct]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.classList.add('modal-open');
        } else {
            document.body.style.overflow = '';
            document.body.classList.remove('modal-open');
        }
        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('modal-open');
        };
    }, [isOpen]);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: '',
                phone: '',
                message: '',
                product: initialProduct,
                units: '1'
            });
            setErrors({});
        }
    }, [isOpen, initialProduct]);

    // Validate form fields
    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) newErrors.name = 'El nombre es requerido';
        if (!formData.phone.trim()) newErrors.phone = 'El teléfono es requerido';
        else if (!/^[\d\s\+\-\(\)]{10,}$/.test(formData.phone)) newErrors.phone = 'Ingresa un número de teléfono válido';
        if (!formData.message.trim()) newErrors.message = 'El mensaje es requerido';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission and redirect to WhatsApp
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);

        // Format message for WhatsApp
        const formattedMessage = `*Nueva Cotización - Print Studio*%0A%0A` +
            `*Nombre:* ${formData.name}%0A` +
            `*Teléfono:* ${formData.phone}%0A` +
            `*Producto de interés:* ${formData.product || 'No especificado'}%0A` +
            `*Unidades:* ${formData.units}%0A` +
            `*Mensaje:*%0A${formData.message}%0A%0A` +
            `_Este mensaje fue enviado desde el sitio web de Print Studio_`;

        // Open WhatsApp with pre-filled message
        const whatsappUrl = `https://wa.me/+${ENV.WHATSAPP_NUMBER}?text=${formattedMessage}`;
        window.open(whatsappUrl, '_blank');

        // Reset and close after delay
        setTimeout(() => {
            setIsSubmitting(false);
            setFormData({
                name: '', phone: '', message: '', product: initialProduct, units: '1'
            });
            onClose();
        }, 1000);
    };

    // Handle input changes and clear corresponding errors
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    // Handle quick product selection
    const handleProductSelect = (productName) => {
        setFormData(prev => ({ ...prev, product: productName }));
    };

    // Handle unit increment
    const incrementUnits = () => {
        const currentUnits = parseInt(formData.units);
        setFormData(prev => ({ ...prev, units: (currentUnits + 1).toString() }));
    };

    // Handle unit decrement
    const decrementUnits = () => {
        const currentUnits = parseInt(formData.units);
        if (currentUnits > 1) {
            setFormData(prev => ({ ...prev, units: (currentUnits - 1).toString() }));
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[99999] flex items-center justify-center p-4"
                onClick={onClose}
            >
                {/* Form Container */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl z-10">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <MessageCircle className="text-primary" size={24} />
                                Cotizar por WhatsApp
                            </h2>
                            <button
                                onClick={onClose}
                                className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <p className="text-gray-600 text-sm">
                            Completa el formulario y te contactaremos por WhatsApp para enviarte una cotización personalizada.
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        {/* Name Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <User size={16} className="inline mr-1" />
                                Nombre completo *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all`}
                                placeholder="Ingresa tu nombre"
                            />
                            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>

                        {/* Phone Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Phone size={16} className="inline mr-1" />
                                Teléfono *
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all`}
                                placeholder="Ej: +52 55 1234 5678"
                            />
                            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                        </div>

                        {/* Product Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Package size={16} className="inline mr-1" />
                                Producto de interés
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="product"
                                    value={formData.product}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                    placeholder="Escribe o selecciona un producto"
                                    list="product-options"
                                />
                            </div>
                        </div>

                        {/* Units */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Hash size={16} className="inline mr-1" />
                                Unidades
                            </label>
                            <div className="flex items-center">
                                <button
                                    type="button"
                                    onClick={decrementUnits}
                                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 w-10 h-10 rounded-l-lg flex items-center justify-center font-bold"
                                >
                                    -
                                </button>
                                <input
                                    type="number"
                                    name="units"
                                    min="1"
                                    value={formData.units}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border-t border-b border-gray-300 text-center outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                                <button
                                    type="button"
                                    onClick={incrementUnits}
                                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 w-10 h-10 rounded-r-lg flex items-center justify-center font-bold"
                                >
                                    +
                                </button>
                            </div>
                        </div>

                        {/* Message Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <MessageCircle size={16} className="inline mr-1" />
                                Mensaje *
                            </label>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                rows="4"
                                className={`w-full px-4 py-3 rounded-lg border ${errors.message ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none`}
                                placeholder="Describe tu proyecto, especificaciones, colores, etc..."
                            />
                            {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                        </div>

                        {/* Submit Button */}
                        <motion.button
                            type="submit"
                            disabled={isSubmitting}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`w-full py-4 rounded-lg font-bold flex items-center justify-center gap-2 transition-all ${isSubmitting
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white'
                                }`}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Procesando...
                                </>
                            ) : (
                                <>
                                    <Send size={20} />
                                    Enviar por WhatsApp
                                </>
                            )}
                        </motion.button>

                        {/* Privacy Note */}
                        <p className="text-gray-500 text-xs text-center mt-4">
                            Al enviar este formulario, aceptas que nos contactemos contigo por WhatsApp para brindarte la cotización.
                        </p>
                    </form>
                </motion.div>
            </motion.div>
        </>
    );
};

export default ContactForm;